import {
  Component,
  input,
  output,
  signal,
  computed,
  forwardRef,
  ChangeDetectionStrategy,
  effect
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

export type WeekDay = 'MO' | 'TU' | 'WE' | 'TH' | 'FR' | 'SA' | 'SU';

export interface WeekDayOption {
  key: WeekDay;
  label: string;
  shortLabel: string;
}

const DEFAULT_WEEK_DAYS: WeekDayOption[] = [
  { key: 'MO', label: 'Lunes', shortLabel: 'L' },
  { key: 'TU', label: 'Martes', shortLabel: 'M' },
  { key: 'WE', label: 'Miércoles', shortLabel: 'X' },
  { key: 'TH', label: 'Jueves', shortLabel: 'J' },
  { key: 'FR', label: 'Viernes', shortLabel: 'V' },
  { key: 'SA', label: 'Sábado', shortLabel: 'S' },
  { key: 'SU', label: 'Domingo', shortLabel: 'D' }
];

@Component({
  selector: 'so-week-day-picker-ui',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './so-week-day-picker.component.html',
  styleUrl: './so-week-day-picker.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SoWeekDayPickerUI),
      multi: true
    }
  ]
})
export class SoWeekDayPickerUI implements ControlValueAccessor {
  // Inputs
  label = input<string>('');
  value = input<WeekDay[]>([]);
  minSelection = input<number>(1);
  maxSelection = input<number>(7);
  showLabels = input<boolean>(false);
  size = input<'sm' | 'md' | 'lg'>('md');
  disabledDays = input<WeekDay[]>([]);

  // Outputs
  selectionChange = output<WeekDay[]>();

  // Internal state
  protected selectedDays = signal<WeekDay[]>([]);
  protected isDisabled = signal<boolean>(false);
  protected weekDays = signal<WeekDayOption[]>(DEFAULT_WEEK_DAYS);

  // Computed
  protected selectedCount = computed(() => this.selectedDays().length);

  constructor() {
    // Sync value input with internal state
    effect(() => {
      const inputValue = this.value();
      if (inputValue && inputValue.length > 0) {
        this.selectedDays.set([...inputValue]);
      }
    }, {  });
  }

  // ControlValueAccessor
  private onChange: (value: WeekDay[]) => void = () => {};
  private onTouched: () => void = () => {};

  writeValue(value: WeekDay[] | null): void {
    if (value && Array.isArray(value)) {
      this.selectedDays.set([...value]);
    } else {
      this.selectedDays.set([]);
    }
  }

  registerOnChange(fn: (value: WeekDay[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled.set(isDisabled);
  }

  protected toggleDay(day: WeekDay): void {
    if (this.isDisabled() || this.isDayDisabled(day)) return;

    const current = this.selectedDays();
    const index = current.indexOf(day);

    if (index === -1) {
      // Add day if not at max
      if (current.length < this.maxSelection()) {
        this.selectedDays.set([...current, day]);
      }
    } else {
      // Remove day if above min
      if (current.length > this.minSelection()) {
        const newSelection = current.filter(d => d !== day);
        this.selectedDays.set(newSelection);
      }
    }

    this.emitChange();
  }

  protected isSelected(day: WeekDay): boolean {
    return this.selectedDays().includes(day);
  }

  protected isDayDisabled(day: WeekDay): boolean {
    return this.disabledDays().includes(day);
  }

  private emitChange(): void {
    const value = this.selectedDays();
    this.onChange(value);
    this.selectionChange.emit(value);
    this.onTouched();
  }
}
